import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiError } from 'api/core/ApiError';
import { FirmsService } from 'api/services/FirmsService';
import { DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY } from 'common/constants/services/firms';
import { DAILY_NAV_REPORT_QUERY_KEY, DAILY_NAV_VALIDATE_QUERY_KEY } from '../useDailyNavReport';

type FinalizeDailyNavDayVariables = {
  date?: string;
  finalize_approver_note?: string;
  staff_finalize_confirmation?: string;
};

const useFinalizeDailyNavDay = (firmId: number | undefined) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ date, finalize_approver_note, staff_finalize_confirmation }: FinalizeDailyNavDayVariables) =>
      FirmsService.apiFirmsDailyNavFinalizeCreate(
        String(firmId),
        { finalize_approver_note, staff_finalize_confirmation },
        date
      ),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_VALIDATE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_QUERY_KEY] });
      enqueueSnackbar(`Finalized ${data.finalized_count} Daily NAV record(s) for ${data.date}.`, {
        variant: 'success',
      });
      if (data.unlinked_companies?.length) {
        enqueueSnackbar(`No finalized valuation to link: ${data.unlinked_companies.join(', ')}.`, {
          variant: 'warning',
        });
      }
    },
    onError: (error: unknown) => {
      let message = 'Failed to finalize Daily NAV.';
      if (error instanceof ApiError && typeof error.body === 'object' && error.body) {
        const { detail } = error.body as Record<string, unknown>;
        if (typeof detail === 'string') {
          message = detail;
        }
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
};

export default useFinalizeDailyNavDay;
